/**
 * API module for event-based gameplay sessions.
 * 
 * This module provides functions to interact with the gameplay session endpoints:
 * - Create or retrieve sessions
 * - Send gameplay events (guesses, hints, etc.)
 * 
 * All functions handle errors gracefully and return structured responses.
 */

import apiClient from './client';
import { captureGameplayAccessOwner, publishGameplayAccess } from '../utils/gameplayAccess';
import { getClientLocalTime } from '../utils/clientTime';

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeSessionResponse = (payload) => {
  const data = isObject(payload?.detail) && payload.detail.access ? payload.detail : payload;
  if (!isObject(data) || !isObject(data.session)) {
    return data;
  }
  return {
    ...data,
    ...data.session,
  };
};

const normalizeEventResponse = (payload) => {
  const data = isObject(payload?.detail) && payload.detail.access ? payload.detail : payload;
  if (!isObject(data) || !isObject(data.event)) {
    return data;
  }
  return {
    ...data,
    ...data.event,
  };
};

/**
 * Create or retrieve an existing gameplay session.
 * 
 * If a session already exists for this puzzle instance, it returns the existing one.
 * If localStorage data is provided via `local_state`, the backend will use it to
 * initialize the session state (on-demand migration). This enables seamless migration
 * of existing game progress without a separate bulk migration step.
 * 
 * @param {string} puzzleInstanceId - UUID of the puzzle instance
 * @param {object} options - Optional parameters
 * @param {object|null} options.local_state - localStorage data to migrate (if any)
 * @param {string|null} token - Optional Clerk JWT token
 * @returns {Promise<{
 *   success: boolean,
 *   session_id?: string,
 *   state?: object,
 *   puzzle_config?: object,
 *   is_new_session?: boolean,
 *   migrated_from_local?: boolean,
 *   error?: string,
 *   message?: string
 * }>}
 * 
 * @example
 * // Create a new session
 * const result = await createOrGetSession(puzzleInstanceId, {}, token);
 * 
 * @example
 * // Create session with localStorage migration
 * const localData = getLocalData('cloze', '2025-01-15');
 * const result = await createOrGetSession(puzzleInstanceId, { local_state: localData }, token);
 */
export async function createOrGetSession(puzzleInstanceId, options = {}, token = null) {
  const owner = captureGameplayAccessOwner();
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const payload = {
      puzzle_instance_id: puzzleInstanceId,
    };
    
    // Include localStorage data for on-demand migration if provided
    if (options.local_state && Object.keys(options.local_state).length > 0) {
      payload.local_state = options.local_state;
    }
    
    const response = await apiClient.post(
      '/gameplay/sessions',
      payload,
      { headers }
    );
    return publishGameplayAccess(normalizeSessionResponse(response.data), owner);
  } catch (error) {
    console.error('Error creating/getting session:', error);
    if (error.response?.data) {
      return publishGameplayAccess(normalizeSessionResponse(error.response.data), owner);
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Get an existing gameplay session by ID.
 * 
 * @param {string} sessionId - UUID of the session
 * @param {string|null} token - Optional Clerk JWT token
 * @returns {Promise<{
 *   success: boolean,
 *   session_id?: string,
 *   state?: object,
 *   puzzle_config?: object,
 *   error?: string,
 *   message?: string
 * }>}
 * 
 * @example
 * const result = await getSession(sessionId, token);
 * if (result.success) {
 *   console.log('Current state:', result.state);
 * }
 */
export async function getSession(sessionId, token = null) {
  const owner = captureGameplayAccessOwner();
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await apiClient.get(
      `/gameplay/sessions/${sessionId}`,
      { headers }
    );
    return publishGameplayAccess(normalizeSessionResponse(response.data), owner);
  } catch (error) {
    console.error('Error getting session:', error);
    if (error.response?.data) {
      return publishGameplayAccess(normalizeSessionResponse(error.response.data), owner);
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Send a gameplay event to the server.
 * 
 * This is the primary way to update game state. All game actions (guesses, hints,
 * submissions, etc.) are sent as events. The server processes the event and returns
 * the updated state.
 * 
 * @param {string} sessionId - UUID of the session
 * @param {string} eventType - Type of event (e.g., 'submit', 'guess', 'hint_reveal_theme')
 * @param {object} eventData - Event-specific data (varies by event type)
 * @param {string|null} token - Optional Clerk JWT token
 * @param {number|null} expectedSeq - Optional sequence number for optimistic concurrency
 * @returns {Promise<{
 *   success: boolean,
 *   error?: string,
 *   message?: string,
 *   state?: object,
 *   result?: object,
 *   app_resources?: {
 *     gettoncini?: { balance: number|null, transactions: array },
 *     learned_terms?: { count: number|null, delta: number, message?: string|null },
 *     stelline?: { balance: number|null, delta: number, message?: string|null },
 *     streak?: object
 *   }
 * }>}
 * 
 * @example
 * // Submit a guess in Cloze - eventData is sent as nested event_data field
 * const result = await sendGameplayEvent(sessionId, 'submit', { words: placements }, token);
 * // This sends: { event_type: 'submit', event_data: { words: [...] }, client_local_time: '...' }
 * 
 * @example
 * // Request a hint in Scalinata
 * const result = await sendGameplayEvent(sessionId, 'hint_definition', { level: 2 }, token);
 */
export async function sendGameplayEvent(sessionId, eventType, eventData = {}, token = null, expectedSeq = null) {
  const owner = captureGameplayAccessOwner();
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const payload = {
      event_type: eventType,
      client_local_time: getClientLocalTime(),
      event_data: eventData,  // Must be nested, not spread!
    };
    
    if (expectedSeq !== null) {
      payload.expected_seq = expectedSeq;
    }
    
    const response = await apiClient.post(
      `/gameplay/sessions/${sessionId}/events`,
      payload,
      { headers }
    );
    return publishGameplayAccess(normalizeEventResponse(response.data), owner);
  } catch (error) {
    console.error('Error sending gameplay event:', error);
    if (error.response?.data) {
      return publishGameplayAccess(normalizeEventResponse(error.response.data), owner);
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Get session statuses for multiple puzzle instances in bulk.
 * 
 * This is used on the homepage to fetch game statuses from the server,
 * enabling cross-device status sync for signed-in users.
 * 
 * @param {string[]} puzzleInstanceIds - Array of puzzle instance UUIDs
 * @param {string|null} token - Optional Clerk JWT token
 * @returns {Promise<{
 *   success: boolean,
 *   statuses?: Array<{puzzle_instance_id: string, status: string|null}>,
 *   error?: string,
 *   message?: string
 * }>}
 * 
 * @example
 * const result = await getSessionStatuses(['uuid1', 'uuid2'], token);
 * if (result.success) {
 *   result.statuses.forEach(item => {
 *     console.log(`Puzzle ${item.puzzle_instance_id}: ${item.status || 'not_started'}`);
 *   });
 * }
 */
export async function getSessionStatuses(puzzleInstanceIds, token = null) {
  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await apiClient.post(
      '/gameplay/sessions/statuses',
      { puzzle_instance_ids: puzzleInstanceIds },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching session statuses:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}


// =============================================================================
// Preview API (Admin Only)
// =============================================================================

/**
 * Create a preview session for admin puzzle testing.
 * 
 * @param {string} puzzleId - UUID of the puzzle (not puzzle_instance_id)
 * @param {string} token - Clerk JWT token (required for admin auth)
 * @returns {Promise<{
 *   success: boolean,
 *   preview_session_id?: string,
 *   state?: object,
 *   puzzle_config?: object,
 *   game_slug?: string,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function createPreviewSession(puzzleId, token) {
  try {
    const headers = {
      'Authorization': `Bearer ${token}`,
    };
    
    const response = await apiClient.post(
      '/gameplay/preview',
      { puzzle_id: puzzleId },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating preview session:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Get a preview session by ID.
 * 
 * @param {string} previewSessionId - UUID of the preview session
 * @param {string} token - Clerk JWT token (required for admin auth)
 * @returns {Promise<{
 *   success: boolean,
 *   state?: object,
 *   puzzle_config?: object,
 *   game_slug?: string,
 *   solutions?: array,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function getPreviewSession(previewSessionId, token) {
  try {
    const headers = {
      'Authorization': `Bearer ${token}`,
    };
    
    const response = await apiClient.get(
      `/gameplay/preview/${previewSessionId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting preview session:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Send a gameplay event to a preview session.
 * 
 * @param {string} previewSessionId - UUID of the preview session
 * @param {string} eventType - Type of event (e.g., 'submit', 'guess')
 * @param {object} eventData - Event-specific data
 * @param {string} token - Clerk JWT token (required for admin auth)
 * @returns {Promise<{
 *   success: boolean,
 *   state?: object,
 *   result?: object,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function sendPreviewEvent(previewSessionId, eventType, eventData, token) {
  try {
    const headers = {
      'Authorization': `Bearer ${token}`,
    };
    
    const payload = {
      event_type: eventType,
      client_local_time: getClientLocalTime(),
      event_data: eventData,
    };
    
    const response = await apiClient.post(
      `/gameplay/preview/${previewSessionId}/events`,
      payload,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending preview event:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/** Read eligibility without starting a session or reserving an allowance slot. */
export async function getGameplayAccess(puzzleInstanceId = null) {
  try {
    const response = await apiClient.get('/gameplay/access', {
      params: puzzleInstanceId ? { puzzle_instance_id: puzzleInstanceId } : {},
    });
    return response.data;
  } catch (error) {
    return { success: false, error: 'NETWORK_ERROR' };
  }
}
